import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Card, CardContent } from '@/components/ui/card';
import { startSettingsTour } from '@/lib/welcomeTour';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ArrowLeft,
  HelpCircle,
  FileText,
  Users,
  Package,
  Bell,
  CreditCard,
  Trash2,
  Share2,
  Download,
  Smartphone,
  Settings,
  UserX,
  Mail,
  Lock,
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  icon?: React.ReactNode;
}

const faqCategories: { title: string; icon: React.ReactNode; items: FAQItem[] }[] = [
  {
    title: 'Primeiros passos',
    icon: <Smartphone className="h-5 w-5" />,
    items: [
      {
        question: 'Como criar meu primeiro orçamento?',
        answer: 'Na tela inicial, toque no botão "+" ou vá em "Orçamentos" e toque em "Novo Orçamento". Preencha os dados do cliente, adicione os itens/serviços e salve ou gere o PDF.',
      },
      {
        question: 'Posso usar o app sem criar conta?',
        answer: 'Sim! Você pode criar um orçamento e gerar o PDF sem conta. Porém, para salvar seus orçamentos e acessá-los depois, é necessário criar uma conta gratuita.',
      },
      {
        question: 'Como personalizar minha empresa no app?',
        answer: 'Vá em "Configurações" > aba "Empresas" e adicione ou edite sua empresa. Você pode incluir logo, CNPJ/CPF, endereço, telefone, e-mail e chave PIX. Essas informações aparecerão nos seus orçamentos.',
      },
    ],
  },
  {
    title: 'Orçamentos',
    icon: <FileText className="h-5 w-5" />,
    items: [
      {
        question: 'Como enviar um orçamento para o cliente?',
        answer: 'Após salvar o orçamento, você pode: 1) Baixar o PDF e enviar por WhatsApp ou e-mail; 2) Compartilhar o link público do orçamento, onde o cliente pode visualizar, aprovar ou rejeitar online.',
      },
      {
        question: 'O cliente pode aprovar o orçamento online?',
        answer: 'Sim! Ao compartilhar o link público, o cliente pode visualizar todos os detalhes e aprovar ou rejeitar o orçamento. Você receberá uma notificação quando isso acontecer.',
      },
      {
        question: 'Como saber se meu orçamento foi visualizado?',
        answer: 'Quando o cliente abre o link público do orçamento, você recebe uma notificação de "visualização". Acesse a central de notificações (ícone do sino) para ver.',
      },
      {
        question: 'Posso editar um orçamento já enviado?',
        answer: 'Sim, você pode editar orçamentos a qualquer momento. Basta ir em "Orçamentos", selecionar o desejado e fazer as alterações. O link público será atualizado automaticamente.',
      },
      {
        question: 'Como adicionar fotos aos itens do orçamento?',
        answer: 'Ao adicionar ou editar um item, toque no ícone de câmera para capturar ou selecionar uma foto. Essa função está disponível para usuários PRO.',
      },
    ],
  },
  {
    title: 'Clientes',
    icon: <Users className="h-5 w-5" />,
    items: [
      {
        question: 'Como salvar um cliente na lista?',
        answer: 'Ao criar um orçamento, marque a opção "Salvar cliente na minha lista". Ou vá em "Clientes" e adicione manualmente.',
      },
      {
        question: 'Como salvar um cliente na agenda do celular?',
        answer: 'Em um orçamento com dados do cliente, quando você digitar 11 dígitos no campo WhatsApp/Telefone, aparecerá o botão "Salvar na Agenda". Toque nele para salvar diretamente na agenda do Android.',
      },
    ],
  },
  {
    title: 'Catálogo',
    icon: <Package className="h-5 w-5" />,
    items: [
      {
        question: 'Para que serve o catálogo?',
        answer: 'O catálogo armazena seus serviços e produtos mais usados. Assim, ao criar um orçamento, você pode adicioná-los rapidamente sem precisar digitar tudo novamente.',
      },
      {
        question: 'Como adicionar um item ao catálogo?',
        answer: 'Você pode: 1) Ir em "Catálogo" e adicionar diretamente; 2) Ao criar um orçamento, marcar "Salvar este item no meu catálogo" para itens manuais.',
      },
    ],
  },
  {
    title: 'Notificações',
    icon: <Bell className="h-5 w-5" />,
    items: [
      {
        question: 'Que tipos de notificações posso receber?',
        answer: 'Você recebe notificações quando um cliente: 1) Visualiza seu orçamento; 2) Aprova o orçamento (com assinatura); 3) Rejeita o orçamento (com motivo opcional).',
      },
      {
        question: 'Não estou recebendo notificações push. O que fazer?',
        answer: 'Verifique se: 1) As notificações estão permitidas nas configurações do seu celular para o app Orçamigo; 2) O modo "Não Perturbe" está desativado; 3) Você está logado na sua conta.',
      },
    ],
  },
  {
    title: 'Assinatura PRO',
    icon: <CreditCard className="h-5 w-5" />,
    items: [
      {
        question: 'Quais são os benefícios do plano PRO?',
        answer: 'Com o PRO você tem: orçamentos ilimitados, fotos nos itens, múltiplas empresas, remoção da marca d\'água do app e acesso a recursos exclusivos futuros.',
      },
      {
        question: 'Como assinar o plano PRO?',
        answer: 'Toque no banner "Seja PRO" na tela inicial ou vá em "Configurações" > "Plano Atual" e toque em "Upgrade". Você será direcionado para a página de pagamento.',
      },
      {
        question: 'Como cancelar minha assinatura?',
        answer: 'As assinaturas são gerenciadas pela Google Play Store. Vá em: Google Play > Menu > Assinaturas > Orçamigo > Cancelar assinatura.',
      },
    ],
  },
  {
    title: 'Conta e privacidade',
    icon: <Lock className="h-5 w-5" />,
    items: [
      {
        question: 'Como alterar minha senha?',
        answer: 'Vá em "Configurações" > aba "Minha Conta" > "Alterar Senha". Digite a nova senha e confirme.',
      },
      {
        question: 'Como excluir minha conta?',
        answer: 'Existem duas formas: 1) Vá em "Configurações" > aba "Minha Conta" > "Excluir Conta"; 2) Acesse a página de solicitação de exclusão: /delete-account. Atenção: esta ação é irreversível e todos os seus dados serão apagados permanentemente.',
      },
      {
        question: 'Meus dados estão seguros?',
        answer: 'Sim! Utilizamos criptografia e servidores seguros para proteger suas informações. Consulte nossa Política de Privacidade para mais detalhes.',
      },
    ],
  },
];

export default function HelpPage() {
  const navigate = useNavigate();

  return (
    <MainLayout showFab={false}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-primary" />
              Central de Ajuda
            </h1>
          </div>
        </div>

        {/* Intro Card */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">
              Encontre respostas para as dúvidas mais frequentes sobre o Orçamigo.
              Se precisar de mais ajuda, entre em contato conosco.
            </p>
          </CardContent>
        </Card>

        {/*
          O tour de AJUSTES mora aqui, sob demanda.

          Ele saiu do 1º login (que agora termina no primeiro orçamento — ver
          src/lib/welcomeTour.ts), mas não foi descartado: configurar conta e
          empresa continua sendo o que a pessoa procura depois, e "depois" é
          exatamente a Central de ajuda.
        */}
        <Card>
          <CardContent className="flex items-center gap-3 pt-4">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <Settings className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Tour dos ajustes</p>
              <p className="text-xs text-muted-foreground">
                Um passo a passo por conta, perfil e cadastro da empresa.
              </p>
            </div>
            <Button3D
              size="sm"
              className="shrink-0"
              onClick={() => {
                startSettingsTour();
                navigate('/dashboard');
              }}
            >
              Ver
            </Button3D>
          </CardContent>
        </Card>

        {/* FAQ Categories */}
        <div className="space-y-4">
          {faqCategories.map((category, categoryIndex) => (
            <Card key={categoryIndex}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    {category.icon}
                  </div>
                  <h2 className="font-semibold text-lg">{category.title}</h2>
                </div>
                
                <Accordion type="single" collapsible className="w-full">
                  {category.items.map((item, itemIndex) => (
                    <AccordionItem 
                      key={itemIndex} 
                      value={`${categoryIndex}-${itemIndex}`}
                      className="border-b-0"
                    >
                      <AccordionTrigger className="text-left text-sm hover:no-underline py-3">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground pb-3">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Account Deletion Section */}
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-destructive" />
              <h3 className="font-semibold text-destructive">Excluir conta</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Se você deseja excluir sua conta permanentemente, existem duas opções:
            </p>
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Settings className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Pelo app:</strong> Vá em Configurações → Minha Conta → Excluir Conta
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Pela página dedicada:</strong> Acesse a página de solicitação abaixo
                </span>
              </li>
            </ul>
            <Link to="/delete-account">
              <Button3D color="red" className="w-full mt-2">
                <Trash2 className="h-4 w-4 mr-2" />
                Solicitar exclusão de conta
              </Button3D>
            </Link>
          </CardContent>
        </Card>

        {/* Data Deletion Section */}
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              <h3 className="font-semibold text-amber-700">Limpar dados</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Se você deseja apagar todos os seus dados (orçamentos, clientes, catálogo) mas manter sua conta:
            </p>
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <Settings className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Pelo app:</strong> Vá em Configurações → Minha Conta → Limpar Todos os Dados
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Pela página dedicada:</strong> Acesse a página de solicitação abaixo
                </span>
              </li>
            </ul>
            <Link to="/delete-data">
              <Button3D color="red" className="w-full mt-2">
                <Trash2 className="h-4 w-4 mr-2" />
                Solicitar limpeza de dados
              </Button3D>
            </Link>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <div className="flex flex-col gap-2">
          <Link to="/terms">
            <Button variant="ghost" className="w-full justify-start">
              <FileText className="h-4 w-4 mr-2" />
              Termos de uso
            </Button>
          </Link>
          <Link to="/privacy">
            <Button variant="ghost" className="w-full justify-start">
              <Lock className="h-4 w-4 mr-2" />
              Política de privacidade
            </Button>
          </Link>
        </div>
      </div>
    </MainLayout>
  );
}
